import type { DocPage } from '../types'
import { media } from '../media'


export const textCsv: DocPage = {
  id: 'text-csv',
  title: 'Text / CSV / Excel',
  description:
    'Configure tabular sources and scratch outputs across General, Text/CSV, Excel, and Geometry tabs, including delimiters, worksheets, and geometry columns.',
  category: 'Formats',
  sections: [
    { id: 'tabs', title: 'Configuration tabs' },
    { id: 'general', title: 'General' },
    { id: 'csv', title: 'Text / CSV' },
    { id: 'excel', title: 'Excel' },
    { id: 'geometry', title: 'Geometry' },
    { id: 'scratch-out', title: 'Scratch output' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'tabs',
      text: 'Configuration tabs',
    },
    {
      type: 'paragraph',
      text: 'The Text/CSV/Excel form is organized into General, Text/CSV, Excel, and Geometry tabs. Complete the tabs that apply to your file type, then submit to load collections (files or Excel worksheets).',
    },
    {
      type: 'figure',
      src: media.sourceConnectionTextCsv,
      alt: 'Text CSV Excel configuration tabs',
      caption:
        'Figure: Text/CSV/Excel source connection form with General, Text/CSV, Excel, and Geometry options.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'general',
      text: 'General',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['File / folder / ZIP picker', 'Locate CSV, TXT, Excel, or packaged tabular inputs.'],
        ['EPSG (geometry CRS)', 'Coordinate reference for geometry columns when present.'],
        ['Override Existing', 'Target overwrite when writing outputs that already exist.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'csv',
      text: 'Text / CSV',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Delimiter', 'Field separator; auto-detect when available.'],
        ['Encoding', 'Character encoding; auto-detect when available.'],
        ['Header row', '0-based header row index.'],
        ['Decimal separator', 'Character used for decimal fractions.'],
        ['Thousands separator', 'Character used for thousands grouping.'],
        ['Use quotes', 'Enable quoting of fields.'],
        ['Quote character', 'Quote character when Use quotes is on.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'excel',
      text: 'Excel',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Worksheets', 'Choose which sheets become collections when connecting an .xlsx workbook.'],
        ['Sheet as collection', 'Each selected worksheet appears as its own collection in the model tree.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'geometry',
      text: 'Geometry',
    },
    {
      type: 'table',
      headers: ['Mode', 'Description'],
      rows: [
        ['WKT column', 'Read geometry from a Well-Known Text column.'],
        ['Coordinate column', 'Single combined coordinate column.'],
        ['X / Y', 'Separate longitude/easting and latitude/northing columns.'],
        ['X / Y / Z', 'Separate X, Y, and optional Z columns.'],
        ['Auto-detect geometry', 'Let the form infer geometry columns when offered.'],
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'CRS',
      text: 'Set EPSG (geometry CRS) on General whenever you define geometry columns so transfers and map-related validation use the correct CRS.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch-out',
      text: 'Scratch output',
    },
    {
      type: 'paragraph',
      text: 'When Text/CSV/Excel is the scratch Target, configure how files are written:',
    },
    {
      type: 'table',
      headers: ['Option group', 'Choices'],
      rows: [
        ['Output formats', 'CSV, Text (.txt), Excel (.xlsx)'],
        ['Destination packaging', 'Single file, Target folder, ZIP'],
        [
          'Geometry export',
          'Per Point / Line / Area geometry export modes as offered by the form',
        ],
        [
          'Text/CSV write settings',
          'Reuse delimiter, encoding, quotes, and separators appropriate to the chosen output format',
        ],
      ],
    },
  ],
}
